import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Plus,
  Upload,
  Save,
  Loader,
  Link,
  Search,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { BoQApi } from "api/boq/service";

const StockUpdateForm = ({
  onClose,
  materialId,
  currentStock,
  onStockUpdate,
  materialDetails,
  editMode = false,
  stockData = {},
}) => {
  const alert = useAlert();
  const [formData, setFormData] = useState({
    update_date: new Date().toISOString().split("T")[0],
    entered_quantity: "",
    comment: "",
    material: materialId,
  });

  // Calculate initial stock action based on quantity (for edit mode)
  const initialStockAction = stockData?.entered_quantity > 0 ? "added" : "used";
  const [stockAction, setStockAction] = useState(initialStockAction);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Task linking states
  const [boqItems, setBoqItems] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const getNormalizedTaskStatus = (item) =>
    String(
      item?.task_status ||
        item?.taskStatus ||
        item?.status ||
        item?.task_details?.task_status ||
        item?.task_details?.status ||
        "",
    ).toLowerCase();
  const isCompletedTask = (item) =>
    getNormalizedTaskStatus(item).includes("complete");

  // Fetch BoQ items that are linked to tasks
  useEffect(() => {
    const fetchBoqData = async () => {
      try {
        const sectionsWithItems = await BoQApi.getSectionsWithItems();
        // Flatten and filter items that have a linked task
        const linkedItems = sectionsWithItems.flatMap((section) =>
          section.items
            .filter((item) => item.task !== null)
            .map((item) => ({
              ...item,
              sectionName: section.section_name,
            })),
        );
        setBoqItems(linkedItems);
      } catch (error) {
        console.error("Error fetching BoQ items:", error);
      }
    };

    fetchBoqData();
  }, []);

  // Set selected task in edit mode if it exists
  useEffect(() => {
    if (editMode && stockData?.task_id && boqItems.length > 0) {
      const item = boqItems.find(
        (i) => i.task === stockData.task_id && !isCompletedTask(i),
      );
      if (item) {
        setSelectedTask(item);
      }
    }
  }, [editMode, stockData, boqItems]);

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && stockData) {
      setFormData({
        update_date:
          stockData.update_date || new Date().toISOString().split("T")[0],
        entered_quantity: Math.abs(stockData.entered_quantity) || "",
        comment: stockData.comment || "",
        material: materialId,
      });

      // Set stock action based on the sign of entered_quantity
      setStockAction(stockData.entered_quantity > 0 ? "added" : "used");
    }
  }, [editMode, stockData, materialId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      update_date: value,
    }));
    setShowDatePicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      const finalQuantity =
        stockAction === "added"
          ? Number(formData.entered_quantity)
          : -Number(formData.entered_quantity);

      if (editMode) {
        // Update existing stock

        const response = await fetch(
          `${BASE_URL}/inventory/update-stock/${stockData.id}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken.trim()}`,
            },
            body: JSON.stringify({
              ...formData,
              entered_quantity: finalQuantity,
              task_id: selectedTask?.task || null,
            }),
          },
        );

        if (response.ok) {
          const updatedStockData = {
            ...stockData,
            ...formData,
            entered_quantity: finalQuantity,
            task_id: selectedTask?.task || null,
          };
          // onStockUpdate(updatedStockData);
          alert.addAlert("Stock updated successfully", "success");
          onClose();
        } else {
          const errorData = await response.json();
          alert.addAlert(
            `Failed to update stock: ${errorData.message || "Unknown error"}`,
            "error",
          );
        }
      } else {
        // Create new stock
        const response = await fetch(`${BASE_URL}/inventory/create-stock/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken.trim()}`,
          },
          body: JSON.stringify({
            ...formData,
            entered_quantity: finalQuantity,
            task_id: selectedTask?.task || null,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          onStockUpdate({
            ...data,
            entered_quantity: finalQuantity,
            created_on: new Date().toISOString(),
          });
          alert.addAlert("Stock added successfully", "success");
          onClose();
        } else {
          const errorData = await response.json();
          alert.addAlert(
            `Failed to create stock: ${errorData.message || "Unknown error"}`,
            "error",
          );
        }
      }
    } catch (error) {
      console.error("Error handling stock:", error);
      alert.addAlert(`Error: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const validateQuantity = (quantity) => {
    const q = Number(quantity);

    // ❌ bail if nothing or zero/negative
    if (q < 0) return false; // <-- NEW line

    // for “used” we must still respect available stock
    if (stockAction === "used") {
      if (editMode && stockData.entered_quantity < 0) {
        const availableStock =
          currentStock + Math.abs(stockData.entered_quantity);
        return q <= availableStock;
      }
      return q <= currentStock;
    }
    return true;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const options = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return d.toLocaleDateString("en-US", options);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const filteredBoqItems = boqItems.filter(
    (item) =>
      !isCompletedTask(item) &&
      ((item.task_name || item.item_name || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
        item.sectionName.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const getTaskStatusBucket = (item) => {
    const normalizedStatus = getNormalizedTaskStatus(item);

    if (
      normalizedStatus.includes("halt") ||
      normalizedStatus.includes("hold") ||
      normalizedStatus.includes("pause")
    ) {
      return "halted";
    }
    if (
      normalizedStatus.includes("not started") ||
      normalizedStatus.includes("not_started")
    ) {
      return "notStarted";
    }
    if (
      normalizedStatus.includes("ongoing") ||
      normalizedStatus.includes("in progress") ||
      normalizedStatus.includes("in_progress")
    ) {
      return "ongoing";
    }
    return "notStarted";
  };

  const groupedBoqItems = [
    { key: "ongoing", title: "Ongoing" },
    { key: "notStarted", title: "Not Started" },
    { key: "halted", title: "Halted" },
  ]
    .map((group) => ({
      ...group,
      items: filteredBoqItems.filter(
        (item) => getTaskStatusBucket(item) === group.key,
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/55" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#F8FAFC] shadow-2xl border-l border-slate-200">
        <div className="h-full overflow-y-auto">
          <div className="sticky top-0 z-10 h-16 bg-gradient-to-r from-slate-50 via-white to-blue-50 flex items-center justify-center border-b border-slate-200">
            <h2 className="text-xl font-semibold tracking-tight text-slate-800">
              {editMode ? "Edit Stock Entry" : "Stock Update"}
            </h2>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 rounded-full p-1.5 hover:bg-slate-100 transition-colors"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>
          <div className="px-4 py-3 pb-4">
            <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
              <div className="space-y-3.5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Material
                  </label>
                  <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-2">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div className="self-center text-sm text-slate-600">
                        <span className="font-medium text-slate-800">
                          Material Name:
                        </span>{" "}
                        {materialDetails?.material_name ||
                          materialDetails?.name ||
                          "Material"}
                      </div>
                      <div className="flex justify-end">
                        <div className="inline-flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                          <span className="font-medium">In Stock:</span>
                          <span>
                            {currentStock} {materialDetails.uom}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-slate-600">
                        <span className="font-medium text-slate-800">
                          Subcategory:
                        </span>{" "}
                        {materialDetails?.subcategory || "-"}
                      </div>
                      <div className="text-sm text-slate-600 justify-self-end pr-8">
                        <span className="font-medium text-slate-800">
                          Brand:
                        </span>{" "}
                        {materialDetails?.brand_name ||
                          materialDetails?.brand ||
                          "-"}
                      </div>
                      <div className="col-span-2 text-sm text-slate-600">
                        <span className="font-medium text-slate-800">
                          Specification:
                        </span>{" "}
                        {materialDetails?.specification || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1 p-1 bg-slate-200/70 rounded-xl">
                    <button
                      type="button"
                      className={`inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                        stockAction === "added"
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-white"
                      }`}
                      onClick={() => setStockAction("added")}
                    >
                      Added
                    </button>
                    <button
                      type="button"
                      className={`inline-flex items-center px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                        stockAction === "used"
                          ? "bg-rose-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-white"
                      }`}
                      onClick={() => setStockAction("used")}
                    >
                      Used
                    </button>
                  </div>
                  <div className="text-xs text-slate-500">
                    {stockAction === "added"
                      ? "Add stock to inventory"
                      : "Remove stock from inventory"}
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-3">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                        <Plus size={16} className="text-slate-400" />
                      </div>
                      <input
                        type="number"
                        name="entered_quantity"
                        value={formData.entered_quantity}
                        onChange={(e) => {
                          if (validateQuantity(e.target.value)) {
                            handleInputChange(e);
                          }
                        }}
                        placeholder="Enter Quantity"
                        className={`w-full px-9 py-2 bg-white border rounded-xl shadow-sm ${
                          !validateQuantity(formData.entered_quantity)
                            ? "border-red-500 focus:ring-red-400"
                            : ""
                        }`}
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                    {!validateQuantity(formData.entered_quantity) && (
                      <p className="text-red-500 text-xs mt-1">
                        Cannot use more than available stock
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={materialDetails.uom}
                      disabled
                      className="w-full p-2 bg-white border border-slate-200 rounded-xl text-slate-700"
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Update Date
                  </label>
                  <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex justify-between items-center">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">
                        {formatDate(formData.update_date)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="flex items-center text-slate-600 text-sm px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                      onClick={toggleDatePicker}
                    >
                      Change
                      <Calendar size={16} className="ml-1" />
                    </button>
                  </div>

                  {showDatePicker && (
                    <div className="absolute z-10 mt-1 bg-white p-4 rounded-xl shadow-xl border border-slate-200 w-full">
                      <label className="block text-sm text-slate-600 mb-2">
                        Select Date
                      </label>
                      <input
                        type="date"
                        name="update_date"
                        value={formData.update_date}
                        onChange={handleDateChange}
                        className="w-full p-2 border rounded-md"
                        max={new Date().toISOString().split("T")[0]}
                      />
                      <div className="flex justify-end mt-3">
                        <button
                          type="button"
                          className="bg-slate-200 text-slate-700 px-3 py-1 rounded-md text-sm mr-2"
                          onClick={() => setShowDatePicker(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
                          onClick={() => setShowDatePicker(false)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {stockAction === "used" && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Link to Task (Optional)
                    </label>
                    <div
                      onClick={() =>
                        boqItems.length > 0 && setShowTaskModal((v) => !v)
                      }
                      className={`flex items-center justify-between p-3 bg-white rounded-xl transition-colors border border-dashed shadow-sm ${
                        boqItems.length > 0
                          ? "cursor-pointer hover:bg-slate-50 border-slate-300"
                          : "cursor-not-allowed border-slate-200 opacity-70"
                      }`}
                    >
                      {selectedTask ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-blue-700">
                            {selectedTask.task_name || selectedTask.item_name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {selectedTask.sectionName}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center text-slate-500 text-sm">
                          <Link size={14} className="mr-2" />
                          {boqItems.length > 0
                            ? "Select a task to link this usage"
                            : "No active tasks available to link"}
                        </div>
                      )}
                      <button
                        type="button"
                        className={`text-xs font-semibold ${
                          boqItems.length > 0
                            ? "text-blue-600"
                            : "text-slate-400"
                        }`}
                      >
                        {boqItems.length > 0
                          ? selectedTask
                            ? "Change"
                            : "Link"
                          : "Unavailable"}
                      </button>
                    </div>
                    {selectedTask && (
                      <button
                        type="button"
                        onClick={() => setSelectedTask(null)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove link
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Remarks/Comments (Optional)
                  </label>
                  <textarea
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    placeholder="Type"
                    rows="1"
                    className="w-full p-3 bg-white border border-slate-200 rounded-xl shadow-sm resize-none"
                  />
                </div>

                {/* Inline Task Selection Panel */}
                {showTaskModal && (
                  <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
                    <div className="p-2.5 border-b flex justify-between items-center bg-slate-50">
                      <h3 className="text-sm font-semibold text-gray-800">
                        Select Task
                      </h3>
                      <button
                        type="button"
                        onClick={() => setShowTaskModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div className="p-2.5 border-b bg-white">
                      <div className="relative">
                        <Search
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          size={16}
                        />
                        <input
                          type="text"
                          placeholder="Search BoQ items or sections..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-gray-100 border-none rounded-md focus:ring-2 focus:ring-purple-500 text-sm"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="overflow-y-auto max-h-64 p-2 bg-white">
                      {groupedBoqItems.map((group) => (
                        <div key={group.key} className="mb-2">
                          <p className="px-1 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            {group.title}
                          </p>
                          {group.items.map((item) => (
                            <div
                              key={item.id}
                              onClick={() => {
                                setSelectedTask(item);
                                setShowTaskModal(false);
                              }}
                              className={`p-3 rounded-md cursor-pointer mb-1 transition-colors ${
                                selectedTask?.id === item.id
                                  ? "bg-purple-50 border border-purple-200"
                                  : "hover:bg-gray-50 border border-transparent"
                              }`}
                            >
                              <div className="text-sm font-medium text-gray-800">
                                {item.task_name || item.item_name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                {item.sectionName}
                              </div>
                              <div className="mt-2 flex items-center justify-between">
                                <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                                  {item.quantity} {item.uom}
                                </span>
                                {selectedTask?.id === item.id && (
                                  <span className="text-[10px] text-purple-600 font-bold uppercase tracking-wider">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}

                      {filteredBoqItems.length === 0 && (
                        <div className="py-8 text-center text-gray-500 text-sm">
                          No matching items found
                        </div>
                      )}
                    </div>

                    <div className="p-2.5 border-t bg-slate-50">
                      <button
                        type="button"
                        onClick={() => setShowTaskModal(false)}
                        className="w-full py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="sticky bottom-0 mt-3 flex items-center justify-end gap-2 border-t border-slate-200 bg-[#F8FAFC] pt-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    !formData.entered_quantity ||
                    !validateQuantity(formData.entered_quantity) ||
                    isLoading
                  }
                  className="bg-gradient-to-r from-[#3B0069] to-[#5B1A8A] text-white px-4 py-2.5 rounded-xl hover:from-[#32005A] hover:to-[#4C1574] transition duration-300 flex items-center justify-center disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed shadow-lg shadow-slate-300/40"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader className="h-5 w-5 animate-spin" />
                      Processing...
                    </div>
                  ) : (
                    <>
                      <span className="mr-2">
                        {editMode ? <Save size={18} /> : <Upload size={18} />}
                      </span>
                      {editMode ? "Save Changes" : "Update Stock"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockUpdateForm;
